import { type Ref, getCurrentScope, onScopeDispose, ref } from 'vue'

import { noop } from '@backmarket/utils/function/noop'

import { useMarqueePause } from './useMarqueePause'
import { useWaaPosition } from './useWaaPosition'

interface UseMarqueeDragOptions {
  rowRef: Ref<HTMLElement | null>
  trackRef: Ref<HTMLElement | null>
  duration: Ref<number>
  dragThreshold?: number
  resumeDelay?: number
}

interface UseMarqueeDragReturn {
  isDragging: Ref<boolean>
  onPointerDown: (event: PointerEvent) => void
  onPointerEnter: () => void
  onPointerLeave: () => void
  onFocusIn: () => void
  onFocusOut: () => void
}

// Pointer travel needed to tell a drag from a tap — below this the press
// still counts as a chip click.
const DEFAULT_DRAG_THRESHOLD = 6
// How long the track is held at the dragged position after release before
// auto-scrolling resumes.
const DEFAULT_RESUME_DELAY = 1500

type State = 'idle' | 'paused' | 'dragging' | 'resuming'

export function useMarqueeDrag(
  options: UseMarqueeDragOptions,
): UseMarqueeDragReturn {
  const {
    rowRef,
    trackRef,
    duration,
    dragThreshold = DEFAULT_DRAG_THRESHOLD,
    resumeDelay = DEFAULT_RESUME_DELAY,
  } = options

  const isDragging = ref(false)
  const state = ref<State>('idle')

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  if (prefersReducedMotion) {
    return {
      isDragging,
      onPointerDown: noop,
      onPointerEnter: noop,
      onPointerLeave: noop,
      onFocusIn: noop,
      onFocusOut: noop,
    }
  }

  const waa = useWaaPosition({ trackRef, duration })
  const pause = useMarqueePause({
    state,
    pauseScroll: waa.pauseScroll,
    resumeScroll: waa.resumeScroll,
  })

  let pointerId: number | null = null
  let startX = 0
  let baseTranslate = 0
  let resumeTimer: ReturnType<typeof setTimeout> | null = null

  waa.initWAA()

  function clearResumeTimer() {
    if (resumeTimer !== null) {
      clearTimeout(resumeTimer)
      resumeTimer = null
    }
  }

  function onPointerMove(event: PointerEvent) {
    if (pointerId !== event.pointerId) return

    const deltaX = event.clientX - startX

    if (state.value === 'paused' && Math.abs(deltaX) >= dragThreshold) {
      state.value = 'dragging'
      isDragging.value = true

      baseTranslate = waa.position.current

      const track = trackRef.value
      if (track) track.style.animation = 'none'
      waa.cancelWAA()

      if (rowRef.value?.setPointerCapture) {
        rowRef.value.setPointerCapture(event.pointerId)
      }
      event.preventDefault()
    }

    if (state.value === 'dragging') {
      event.preventDefault()
      waa.setDragTranslate(baseTranslate + deltaX)
    }
  }

  function onPointerUp(event: PointerEvent) {
    if (pointerId !== event.pointerId) return

    pointerId = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerUp)

    if (rowRef.value && rowRef.value.hasPointerCapture?.(event.pointerId)) {
      rowRef.value.releasePointerCapture?.(event.pointerId)
    }

    if (state.value === 'dragging') {
      state.value = 'resuming'
      isDragging.value = false

      clearResumeTimer()
      resumeTimer = setTimeout(() => {
        resumeTimer = null
        waa.startScroll()
        state.value = 'idle'
      }, resumeDelay)
    } else {
      state.value = 'paused'
    }
  }

  function onPointerDown(event: PointerEvent) {
    if (state.value === 'idle') {
      state.value = 'paused'
      waa.pauseScroll()
    }

    if (state.value === 'resuming') {
      clearResumeTimer()
      state.value = 'paused'
      waa.pauseScroll()
    }

    const { pointerId: id, clientX } = event
    pointerId = id
    startX = clientX

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  if (getCurrentScope()) {
    onScopeDispose(() => {
      clearResumeTimer()
      waa.cleanup()
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    })
  }

  return {
    isDragging,
    onPointerDown,
    onPointerEnter: pause.onPointerEnter,
    onPointerLeave: pause.onPointerLeave,
    onFocusIn: pause.onFocusIn,
    onFocusOut: pause.onFocusOut,
  }
}
